import { DeviceType } from '../../devices/components/devices.models';
import { HealthFilterName, HealthFilterPayload } from '../health-api.types';
import { HealthMonitorFilterType } from '../health-monitor.types';

const commonDefaultFilters: readonly DeviceType[] = [
  DeviceType.DCU,
  DeviceType.BaseStation,
  DeviceType.ExactaGuard,
  DeviceType.MTU,
  DeviceType.Meter,
  DeviceType.RMDSensor
];

const allowedDeviceTypesSetForReadRate = new Set([
  DeviceType.ExactaGuard,
  DeviceType.Meter,
  DeviceType.MTU,
  DeviceType.RDD,
  DeviceType.RMDSensor
]);

const allowedDeviceTypesSetForReadSuccessRate = new Set([DeviceType.Meter, DeviceType.MTU]);

const allowedDeviceTypesSetForBatteryCapacity = new Set([
  DeviceType.DCU,
  DeviceType.ExactaGuard,
  DeviceType.Meter,
  DeviceType.MTU,
  DeviceType.RDD,
  DeviceType.RMDSensor
]);

const alarmsStatisticsDefaultFilters: readonly DeviceType[] = [...commonDefaultFilters];

const healthReadRateDefaultFilters: readonly DeviceType[] = commonDefaultFilters.filter(type =>
  allowedDeviceTypesSetForReadRate.has(type)
);

const readSuccessRateDefultFilters: readonly DeviceType[] = commonDefaultFilters.filter(type =>
  allowedDeviceTypesSetForReadSuccessRate.has(type)
);

const batteryCapacityDefaultFilters: readonly DeviceType[] = commonDefaultFilters.filter(type =>
  allowedDeviceTypesSetForBatteryCapacity.has(type)
);

const otherDefaultFilters: readonly DeviceType[] = [...commonDefaultFilters, DeviceType.RDD];

type HealthMonitorWidgetDeviceFiltersMap = ReadonlyMap<HealthMonitorFilterType | 'default', readonly DeviceType[]>;

export enum HealthMonitorWidgetDeviceFilter {
  AlarmsStatistics = 'AlarmsStatistics',
  HealthReadRate = 'HealthReadRate',
  ReadSuccessRate = 'ReadSuccessRate',
  BatteryCapacity = 'BatteryCapacity',
  Other = 'Other'
}

export class HealthMonitorDeviceFilterUtil {
  static readonly #widgetDeviceFiltersMap: ReadonlyMap<
    HealthMonitorWidgetDeviceFilter,
    HealthMonitorWidgetDeviceFiltersMap
  > = new Map([
    [
      HealthMonitorWidgetDeviceFilter.AlarmsStatistics,
      // TODO: Actualize the keys. Rename `mtu' to 'Metering'. Specify the type of the keys.
      new Map([
        ['dcu', [DeviceType.DCU, DeviceType.BaseStation]],
        ['mtu', [DeviceType.Meter, DeviceType.MTU]],
        ['sensors', [DeviceType.RMDSensor, DeviceType.ExactaGuard]],
        ['default', alarmsStatisticsDefaultFilters]
      ])
    ],
    [
      HealthMonitorWidgetDeviceFilter.HealthReadRate,
      new Map([
        ['mtu', [DeviceType.Meter, DeviceType.MTU]],
        ['sensors', [DeviceType.RMDSensor, DeviceType.ExactaGuard]],
        ['default', healthReadRateDefaultFilters]
      ])
    ],
    [
      HealthMonitorWidgetDeviceFilter.ReadSuccessRate,
      new Map([
        ['mtu', [DeviceType.Meter, DeviceType.MTU]],
        ['default', readSuccessRateDefultFilters]
      ])
    ],
    [
      HealthMonitorWidgetDeviceFilter.BatteryCapacity,
      new Map([
        ['dcu', [DeviceType.DCU]],
        ['mtu', [DeviceType.Meter, DeviceType.MTU]],
        ['sensors', [DeviceType.RMDSensor, DeviceType.ExactaGuard]],
        ['default', batteryCapacityDefaultFilters]
      ])
    ],
    [
      HealthMonitorWidgetDeviceFilter.Other,
      new Map([
        ['dcu', [DeviceType.DCU, DeviceType.BaseStation]],
        ['mtu', [DeviceType.Meter, DeviceType.MTU, DeviceType.RDD]],
        ['sensors', [DeviceType.RMDSensor, DeviceType.ExactaGuard]],
        ['default', otherDefaultFilters]
      ])
    ]
  ]);

  static preparePayload(
    widgetFilter: HealthMonitorWidgetDeviceFilter,
    selectedDevicesFilters: readonly HealthMonitorFilterType[]
  ): HealthFilterPayload[] {
    const selectedFilters = HealthMonitorDeviceFilterUtil.#prepareSelectedFilters(selectedDevicesFilters, widgetFilter);
    const filteredValues = selectedFilters.length
      ? selectedFilters
      : HealthMonitorDeviceFilterUtil.#getDefaultFilteredValues(widgetFilter);
    return [
      {
        filterType: HealthFilterName.DeviceTypes,
        filteredValues: [...filteredValues]
      }
    ];
  }

  static #getDefaultFilteredValues(widgetFilter: HealthMonitorWidgetDeviceFilter): readonly DeviceType[] {
    const widgetFiltersMap = HealthMonitorDeviceFilterUtil.#getWidgetDeviceFiltersMap(widgetFilter);
    return widgetFiltersMap.get('default') as readonly DeviceType[];
  }

  static #getWidgetDeviceFiltersMap(
    widgetFilter: HealthMonitorWidgetDeviceFilter
  ): HealthMonitorWidgetDeviceFiltersMap {
    const filterTypesMap = HealthMonitorDeviceFilterUtil.#widgetDeviceFiltersMap.get(widgetFilter);
    if (!filterTypesMap) {
      throw new Error(
        `[AOS. DeviceTypeHealthFilterPayloadUtil] The \`HealthMonitorWidgetDeviceFilter\` ('${widgetFilter}') does not exist.`
      );
    }
    return filterTypesMap;
  }

  static #prepareSelectedFilters(
    selectedDevicesFilters: readonly HealthMonitorFilterType[],
    widgetFilter: HealthMonitorWidgetDeviceFilter
  ): readonly DeviceType[] {
    const deviceTypeFilteredValuesMap = HealthMonitorDeviceFilterUtil.#getWidgetDeviceFiltersMap(widgetFilter);
    const devices: DeviceType[] = [];
    selectedDevicesFilters.forEach((filter: HealthMonitorFilterType): void => {
      const filterDevices: readonly DeviceType[] | undefined = deviceTypeFilteredValuesMap.get(filter);
      if (filterDevices !== undefined) {
        devices.push(...filterDevices);
      }
    });
    return devices;
  }
}
